import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl, getServerUrl } from '../config';
import { getYearLabel as getYearLabelUtil } from '../utils/yearUtils';

const ParentClubBooking = ({ parentEmail, parentName, students, onBack }) => {
  const [bookingPeriods, setBookingPeriods] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [myWaitlist, setMyWaitlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(null); // club id
  const [activeTab, setActiveTab] = useState('available'); // 'available', 'bookings', or 'waitlist'
  const [selectedChild, setSelectedChild] = useState(''); // for filtering available clubs
  const [bookingForm, setBookingForm] = useState({
    student_school_id: '',
    special_requirements: '',
    emergency_contact: '',
    medical_info: ''
  });

  useEffect(() => {
    if (parentEmail) {
      loadData();
    }
  }, [parentEmail]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load clubs for this parent using the correct endpoint
      const clubsRes = await axios.get(buildApiUrl(`/api/clubs/available/${parentEmail}`));
      console.log('Clubs response:', clubsRes.data);
      
      if (clubsRes.data.success && clubsRes.data.booking_periods) {
        // Define day order: Sunday to Thursday
        const dayOrder = {
          'Sunday': 0,
          'Monday': 1,
          'Tuesday': 2,
          'Wednesday': 3,
          'Thursday': 4,
          'Friday': 5,
          'Saturday': 6
        };

        // Filter out closed booking periods (where clubs have ended)
        const now = new Date();
        const openPeriods = clubsRes.data.booking_periods.filter(periodData => {
          const clubsEndDate = new Date(periodData.period.clubs_end_date);
          console.log('[Period Filter]', { 
            name: periodData.period.name, 
            clubs_end_date: periodData.period.clubs_end_date, 
            clubsEndDate, 
            now, 
            isOpen: clubsEndDate >= now 
          });
          return clubsEndDate >= now;
        });

        // Process the booking periods and add missing properties to clubs
        const processedPeriods = openPeriods.map(periodData => ({
          ...periodData.period,
          clubs: periodData.clubs
            .map(club => {
              console.log('[Club Data]', {
                name: club.name,
                selected_students: club.selected_students,
                selected_students_type: typeof club.selected_students,
                selected_students_length: club.selected_students?.length
              });
              return {
                ...club,
                is_full: club.current_bookings >= club.capacity,
                age_groups: club.age_groups || [],
                selected_students: club.selected_students || [],
                location: club.location || '',
                description: club.description || '',
                requirements: club.requirements || ''
              };
            })
            .sort((a, b) => (dayOrder[a.day_of_week] || 999) - (dayOrder[b.day_of_week] || 999)),
          children: periodData.children
        }));
        
        setBookingPeriods(processedPeriods);
        if (processedPeriods.length > 0) {
          setSelectedPeriod(processedPeriods[0]);
        }
      } else {
        console.error('Failed to load clubs:', clubsRes.data.error);
        setBookingPeriods([]);
      }

      // Load my bookings using the correct endpoint
      const bookingsRes = await axios.get(buildApiUrl(`/api/parent/${parentEmail}/bookings`));
      console.log('Bookings response:', bookingsRes.data);
      
      if (bookingsRes.data.success) {
        // Filter out cancelled bookings and expired clubs
        const activeBookings = (bookingsRes.data.bookings || []).filter(
          booking => booking.booking_status !== 'cancelled' && 
                     !isClubExpired(booking.period?.clubs_end_date)
        );
        setMyBookings(activeBookings);
      } else {
        console.error('Failed to load bookings:', bookingsRes.data.error);
        setMyBookings([]);
      }

      // Load my waiting list
      const waitlistRes = await axios.get(buildApiUrl(`/api/parent/${parentEmail}/waitlist`));
      console.log('Waitlist response:', waitlistRes.data);
      
      if (waitlistRes.data.success) {
        // Filter out expired clubs from waitlist
        const activeWaitlist = (waitlistRes.data.waitlist || []).filter(
          entry => !isClubExpired(entry.period?.clubs_end_date)
        );
        setMyWaitlist(activeWaitlist);
      } else {
        console.error('Failed to load waitlist:', waitlistRes.data.error);
        setMyWaitlist([]);
      }
    } catch (error) {
      console.error('Error loading club data:', error);
    } finally {
      setLoading(false);
    }
  };

  const bookClub = async (e) => {
    e.preventDefault();
    if (!showBookingForm) return;

    setLoading(true);
    try {
      // Find the selected child/student from the students prop or bookingPeriods data
      let selectedStudent = null;
      
      // First try to find from students prop
      if (students && students.length > 0) {
        selectedStudent = students.find(student => student.school_id === bookingForm.student_school_id);
      }
      
      // If not found, try to find from bookingPeriods data
      if (!selectedStudent && bookingPeriods && bookingPeriods.length > 0) {
        for (const period of bookingPeriods) {
          if (period.children && period.children.length > 0) {
            selectedStudent = period.children.find(child => child.school_id === bookingForm.student_school_id);
            if (selectedStudent) break;
          }
        }
      }
      
      if (!selectedStudent) {
        alert('Student information not found. Please try again.');
        return;
      }

      // Prepare the complete booking data
      const completeBookingData = {
        ...bookingForm,
        student_name: selectedStudent.full_name || selectedStudent.name,
        parent_email: parentEmail,
        parent_name: parentName || "Parent"
      };

      const response = await axios.post(
        buildApiUrl(`/api/clubs/${showBookingForm}/book?parent_email=${parentEmail}`),
        completeBookingData
      );
      
      if (response.data.success) {
        alert('Club booked successfully! 🎉');
        setShowBookingForm(null);
        setBookingForm({
          student_school_id: '',
          special_requirements: '',
          emergency_contact: '',
          medical_info: ''
        });
        loadData(); // Refresh data
      } else {
        // Check if club is full and offer to join waiting list
        if (response.data.is_full) {
          const waitlistCount = response.data.waitlist_count || 0;
          const message = waitlistCount > 0 
            ? `This club is fully booked. There are ${waitlistCount} people waiting. Would you like to join the waiting list?`
            : `This club is fully booked. Would you like to join the waiting list?`;
          
          if (confirm(message)) {
            // Join waiting list
            try {
              const waitlistRes = await axios.post(
                buildApiUrl(`/api/clubs/${showBookingForm}/waitlist?parent_email=${parentEmail}`),
                completeBookingData
              );
              
              if (waitlistRes.data.success) {
                const position = waitlistRes.data.waitlist?.position || 'unknown';
                alert(`✅ Added to waiting list!\n\nYour position: #${position}\n\nYou will be automatically booked when a spot becomes available.`);
                setShowBookingForm(null);
                setBookingForm({
                  student_school_id: '',
                  special_requirements: '',
                  emergency_contact: '',
                  medical_info: ''
                });
                loadData();
              } else {
                alert('Error joining waiting list: ' + waitlistRes.data.error);
              }
            } catch (waitlistError) {
              console.error('Error joining waiting list:', waitlistError);
              alert('Error joining waiting list. Please try again.');
            }
          }
        } else {
          alert('Error booking club: ' + response.data.error);
        }
      }
    } catch (error) {
      console.error('Error booking club:', error);
      alert('Error booking club. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!confirm('Are you sure you want to cancel this booking?')) return;

    setLoading(true);
    try {
      const response = await axios.delete(
        buildApiUrl(`/api/bookings/${bookingId}/cancel?parent_email=${parentEmail}`)
      );
      
      if (response.data.success) {
        alert('Booking cancelled successfully');
        loadData(); // Refresh data
      } else {
        alert('Error cancelling booking: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Error cancelling booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const leaveWaitlist = async (clubId, studentSchoolId) => {
    if (!confirm('Are you sure you want to leave this waiting list?')) return;

    setLoading(true);
    try {
      const response = await axios.delete(
        buildApiUrl(`/api/clubs/${clubId}/waitlist?student_school_id=${studentSchoolId}`)
      );
      
      if (response.data.success) {
        alert('Removed from waiting list successfully');
        loadData(); // Refresh data
      } else {
        alert('Error leaving waiting list: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error leaving waiting list:', error);
      alert('Error leaving waiting list. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (datetime) => {
    return new Date(datetime).toLocaleDateString('en-GB');
  };

  // Helper function to convert nc_year to year group format
  const getYearGroupFromNcYear = (nc_year) => {
    return getYearLabelUtil(nc_year);
  };

  // Helper function to check if club period has expired
  const isClubExpired = (periodEndDate) => {
    if (!periodEndDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(periodEndDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  // Helper function to check if student is eligible for club based on selected students or age groups
  const isStudentEligibleForClub = (club, student) => {
    // Get student ID - could be either student.school_id or student.id depending on source
    const studentId = student.school_id || student.id;
    
    // Priority 1: Check selected_students (hierarchical selection)
    if (club.selected_students && club.selected_students.length > 0) {
      // Handle both array and string cases (PostgreSQL might return JSON as string)
      const studentIds = Array.isArray(club.selected_students) 
        ? club.selected_students 
        : (typeof club.selected_students === 'string' ? JSON.parse(club.selected_students) : []);
      
      console.log('[Club Eligibility]', {
        clubName: club.name,
        studentIds,
        studentId,
        includes: studentIds.includes(studentId)
      });
      
      return studentIds.includes(studentId);
    }
    
    // Priority 2: Fall back to age_groups (legacy/deprecated)
    if (!club.age_groups || club.age_groups.length === 0) {
      return true; // No restrictions
    }
    
    // Convert student's nc_year to year group format
    const studentYearGroup = getYearGroupFromNcYear(student.nc_year);
    
    // Check if student's year group is in the club's age_groups
    return club.age_groups.includes(studentYearGroup);
  };

  // Helper function to check if two time ranges overlap
  const timesOverlap = (club1, club2) => {
    if (club1.day_of_week !== club2.day_of_week) return false;
    
    // Convert time strings to minutes for comparison
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    const start1 = timeToMinutes(club1.start_time);
    const end1 = timeToMinutes(club1.end_time);
    const start2 = timeToMinutes(club2.start_time);
    const end2 = timeToMinutes(club2.end_time);
    
    // Check for overlap: start1 < end2 AND end1 > start2
    return start1 < end2 && end1 > start2;
  };

  const getStudentConflicts = (club, studentId) => {
    if (!selectedPeriod) return [];
    
    const conflicts = [];
    
    // Check existing bookings from API data (exclude cancelled bookings)
    club.child_bookings?.forEach(booking => {
      if (booking.student_id === studentId && booking.booking_status !== 'cancelled') {
        conflicts.push(booking);
      }
    });
    
    // Check other clubs for time overlaps (only within the same active period)
    selectedPeriod.clubs.forEach(otherClub => {
      if (otherClub.id !== club.id && timesOverlap(club, otherClub)) {
        const hasBooking = otherClub.child_bookings?.find(
          b => b.student_id === studentId && b.booking_status !== 'cancelled'
        );
        if (hasBooking) {
          conflicts.push({
            type: 'time_conflict',
            club_name: otherClub.name,
            ...hasBooking
          });
        }
      }
    });
    
    // Note: We don't check myBookings here because those are from other periods
    // Parents should be able to book clubs at the same time if the previous period has ended
    
    return conflicts;
  };

  const canBookClub = (club, studentId) => {
    const conflicts = getStudentConflicts(club, studentId);
    return conflicts.length === 0 && !club.is_full && selectedPeriod?.booking_open;
  };

  if (loading && bookingPeriods.length === 0) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading club booking information...</p>
        </div>
      </div>
    );
  }

  if (!students || students.length === 0) {
    return (
      <div className="text-center p-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">👨‍👩‍👧‍👦</span>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Students Found</h3>
        <p className="text-gray-600">No students are linked to your account</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <span className="text-2xl">←</span>
            </button>
          )}
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <span className="text-purple-600 text-xl">🏃‍♂️</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Club Booking</h2>
            <p className="text-gray-600">Manage your children's after school activities</p>
          </div>
        </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('available')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'available'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span>🏆</span>
              Available Clubs
            </span>
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'bookings'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span>📋</span>
              My Bookings
              {myBookings.length > 0 && (
                <span className="bg-purple-100 text-purple-600 text-xs rounded-full px-2 py-0.5">
                  {myBookings.length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('waitlist')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'waitlist'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span>⏳</span>
              Waiting List
              {myWaitlist.length > 0 && (
                <span className="bg-yellow-100 text-yellow-600 text-xs rounded-full px-2 py-0.5">
                  {myWaitlist.length}
                </span>
              )}
            </span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'available' && (
        <div className="space-y-6">
          {/* Booking Period and Child Selector */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Booking Period Selector */}
              {bookingPeriods.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Booking Period</label>
                  <select
                    value={selectedPeriod?.id || ''}
                    onChange={(e) => {
                      const period = bookingPeriods.find(bp => bp.id == e.target.value);
                      setSelectedPeriod(period);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {bookingPeriods.map((bp) => (
                      <option key={bp.id} value={bp.id}>
                        {bp.name} {bp.booking_open ? '(Open)' : '(Closed)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Child Selector */}
              {selectedPeriod && selectedPeriod.children.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Filter by Child</label>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setSelectedChild('')}
                      className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                        !selectedChild ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center border-2 border-white shadow">
                        <span className="text-2xl">👨‍👩‍👧‍👦</span>
                      </div>
                      <span className={`mt-2 text-xs font-medium ${!selectedChild ? 'text-purple-700' : 'text-gray-700'}`}>All</span>
                    </button>
                    {selectedPeriod.children.map((child) => {
                      const isSelected = selectedChild === child.school_id;
                      return (
                        <button
                          key={child.school_id}
                          onClick={() => setSelectedChild(child.school_id)}
                          className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                            isSelected ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-gray-50'
                          }`}
                        >
                          <img
                            src={`${getServerUrl()}/student-photo/${child.school_id}?v=3`}
                            alt={child.full_name}
                            onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(child.full_name)}&background=f3e8ff&color=7e22ce&size=64`; }}
                            className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
                          />
                          <span className={`mt-2 text-xs font-medium text-center max-w-[80px] truncate ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                            {child.full_name.split(' ')[0]}
                          </span>
                          {child.form && (
                            <span className="text-xs text-gray-400">{child.form}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {bookingPeriods.length === 0 && (
            <div className="text-center p-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📅</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Club Booking Periods Available</h3>
              <p className="text-gray-600">There are currently no active club booking periods</p>
            </div>
          )}



          {/* Available Clubs */}
          {selectedPeriod && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Available Clubs</h3>
                <p className="text-sm text-gray-600">
                  {selectedChild ? 
                    `Showing clubs for ${selectedPeriod.children.find(c => c.school_id === selectedChild)?.full_name}` : 
                    `All clubs in ${selectedPeriod.name}`
                  }
                </p>
              </div>

              {selectedPeriod.clubs.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🏃‍♂️</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Clubs Available</h3>
                  <p className="text-gray-600">No clubs are available for this booking period</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 p-2 md:p-4">
                  {selectedPeriod.clubs
                    .filter(club => {
                      // If a child is selected, only show clubs they're eligible for
                      if (selectedChild) {
                        const child = selectedPeriod.children.find(c => c.school_id === selectedChild);
                        return child ? isStudentEligibleForClub(club, child) : true;
                      }
                      return true;
                    })
                    .map((club) => (
                    <div key={club.id} className="border border-gray-200 rounded-lg p-3 md:p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-2 md:mb-3">
                        <h4 className="text-base md:text-lg font-semibold text-gray-900 leading-tight pr-2">{club.name}</h4>
                        <div className="text-right flex-shrink-0">
                          <span className={`inline-flex px-1.5 md:px-2 py-1 text-xs font-medium rounded-full ${
                            club.is_full 
                              ? 'bg-red-100 text-red-800' 
                              : club.available_spots <= 5 
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {club.current_bookings}/{club.capacity}
                            <span className="hidden sm:inline">
                              {club.is_full ? ' Full' : ` (${club.available_spots} left)`}
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-gray-600 mb-3 md:mb-4">
                        <div className="flex items-center gap-2">
                          <span>📅</span>
                          <span>{club.day_of_week}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>🕐</span>
                          <span>{club.start_time} - {club.end_time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>👨‍🏫</span>
                          <span className="truncate">{club.teacher_name}</span>
                        </div>
                        {club.location && (
                          <div className="flex items-center gap-2">
                            <span>📍</span>
                            <span className="truncate">{club.location}</span>
                          </div>
                        )}
                        {club.age_groups && (
                          <div className="flex items-center gap-2">
                            <span>🎯</span>
                            <span className="truncate">Ages: {Array.isArray(club.age_groups) ? club.age_groups.join(', ') : club.age_groups}</span>
                          </div>
                        )}
                      </div>

                      {club.description && (
                        <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 bg-gray-50 p-2 md:p-3 rounded">
                          {club.description}
                        </p>
                      )}

                      {club.requirements && (
                        <div className="text-xs text-orange-700 bg-orange-50 p-2 rounded mb-3 md:mb-4">
                          📝 <strong>Requirements:</strong> {club.requirements}
                        </div>
                      )}

                      {/* Student Booking Options */}
                      <div className="space-y-3">
                        {(() => {
                          const eligibleChildren = selectedPeriod.children
                            .filter(child => !selectedChild || child.school_id === selectedChild)
                            .filter(child => isStudentEligibleForClub(club, child));
                          
                          if (eligibleChildren.length === 0) {
                            return (
                              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded text-center">
                                ℹ️ No eligible students for this club's age group
                              </div>
                            );
                          }
                          
                          return eligibleChildren.map((child) => {
                            const conflicts = getStudentConflicts(club, child.school_id);
                            const canBook = canBookClub(club, child.school_id);
                            // Check both club.child_bookings and myBookings to find existing booking
                            const existingBookingInClub = club.child_bookings?.find(
                              b => b.student_id === child.school_id && b.booking_status !== 'cancelled'
                            );
                            const existingBookingInMyBookings = myBookings.find(
                              b => b.club_id === club.id && b.student_school_id === child.school_id && b.booking_status !== 'cancelled'
                            );
                            const existingBooking = existingBookingInClub || existingBookingInMyBookings;
                            const onWaitlist = myWaitlist.find(
                              w => w.club_id === club.id && w.student_school_id === child.school_id
                            );

                            return (
                              <div key={child.school_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-blue-600 text-sm font-medium">
                                      {child.full_name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                    </span>
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900">{child.full_name.split(' ')[0]}</div>
                                    <div className="text-xs text-gray-500">Form {child.form}</div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  {existingBooking ? (
                                    <span className="px-2 py-1 text-xs bg-gray-400 text-white rounded-full">
                                      ✓ Already Booked
                                    </span>
                                  ) : onWaitlist ? (
                                    <span className="px-2 py-1 text-xs bg-yellow-500 text-white rounded-full">
                                      ⏳ On Waitlist (#{onWaitlist.position})
                                    </span>
                                  ) : conflicts.length > 0 ? (
                                    <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                                      ✗ {conflicts[0].type === 'time_conflict' ? 'Time Conflict' : 'Already Booked'}
                                    </span>
                                  ) : !selectedPeriod.booking_open ? (
                                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                                      Booking Closed
                                    </span>
                                  ) : club.is_full && !existingBooking ? (
                                    <div className="flex items-center space-x-2">
                                      <span className="px-2 py-1 text-xs bg-red-500 text-white rounded-full">
                                        Fully Booked
                                      </span>
                                      <button
                                        onClick={() => {
                                          setShowBookingForm(club.id);
                                          setBookingForm({...bookingForm, student_school_id: child.school_id});
                                        }}
                                        disabled={loading}
                                        className="px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 whitespace-nowrap"
                                      >
                                        ⏳ Join Waiting List
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setShowBookingForm(club.id);
                                        setBookingForm({...bookingForm, student_school_id: child.school_id});
                                      }}
                                      disabled={loading}
                                      className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                                    >
                                      Book Now
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* My Bookings Tab */}
      {activeTab === 'bookings' && (
        <div className="space-y-6">
          {myBookings.length === 0 ? (
            <div className="text-center p-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📋</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Bookings Yet</h3>
              <p className="text-gray-600 mb-4">You haven't made any club bookings for your children</p>
              <button
                onClick={() => setActiveTab('available')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Browse Available Clubs
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Group bookings by child */}
              {selectedPeriod && selectedPeriod.children.map(child => {
                const childBookings = myBookings.filter(booking => booking.student_school_id === child.school_id);
                
                if (childBookings.length === 0) return null;

                return (
                  <div key={child.school_id} className="bg-white border border-gray-200 rounded-lg">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {child.full_name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{child.full_name}</h3>
                          <p className="text-sm text-gray-600">Form {child.form} • {childBookings.length} booking(s)</p>
                        </div>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {childBookings.map((booking) => (
                        <div key={booking.id} className="p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{booking.club.name}</h4>
                              <div className="text-sm text-gray-600 space-y-1 mt-2">
                                <div className="flex items-center gap-2">
                                  <span>📅</span>
                                  <span>{booking.club.day_of_week} • {booking.club.start_time} - {booking.club.end_time}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span>👨‍🏫</span>
                                  <span>{booking.club.teacher_name}</span>
                                </div>
                                {booking.club.location && (
                                  <div className="flex items-center gap-2">
                                    <span>📍</span>
                                    <span>{booking.club.location}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end space-y-2 ml-4">
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                booking.booking_status === 'confirmed' 
                                  ? 'bg-green-100 text-green-800'
                                  : booking.booking_status === 'waitlist'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {booking.booking_status === 'confirmed' ? '✓ Confirmed' : 
                                 booking.booking_status === 'waitlist' ? '⏳ Waitlist' : 
                                 booking.booking_status}
                              </span>
                              <button
                                onClick={() => cancelBooking(booking.id)}
                                disabled={loading}
                                className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 px-2 py-1 hover:bg-red-50 rounded"
                              >
                                Cancel Booking
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Waiting List Tab */}
      {activeTab === 'waitlist' && (
        <div className="bg-white rounded-lg shadow p-6">
          {myWaitlist.length === 0 ? (
            <div className="text-center p-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⏳</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Waiting List Entries</h3>
              <p className="text-gray-600 mb-6">You don't have any students on club waiting lists</p>
              <button
                onClick={() => setActiveTab('available')}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Browse Available Clubs
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(
                myWaitlist.reduce((acc, entry) => {
                  const key = entry.student_name;
                  if (!acc[key]) {
                    acc[key] = {
                      student_name: entry.student_name,
                      student_school_id: entry.student_school_id,
                      entries: []
                    };
                  }
                  acc[key].entries.push(entry);
                  return acc;
                }, {})
              ).map(([studentName, { student_school_id, entries }]) => {
                return (
                  <div key={student_school_id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-medium">
                            {studentName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{studentName}</h3>
                          <p className="text-sm text-gray-600">{entries.length} waiting list entr{entries.length === 1 ? 'y' : 'ies'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {entries.map((entry) => (
                        <div key={entry.id} className="p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-medium text-gray-900">{entry.club_name}</h4>
                                <span className="px-3 py-1 text-xs font-bold rounded-full bg-yellow-100 text-yellow-800">
                                  Position #{entry.position}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                {entry.day_of_week && (
                                  <div className="flex items-center gap-2">
                                    <span>📅</span>
                                    <span>{entry.day_of_week} • {entry.start_time} - {entry.end_time}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-2">
                                  <span>🕐</span>
                                  <span>Joined: {formatDateTime(entry.joined_at)}</span>
                                </div>
                                <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded mt-2">
                                  💡 You will be automatically booked when a spot becomes available
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end space-y-2 ml-4">
                              <button
                                onClick={() => leaveWaitlist(entry.club_id, entry.student_school_id)}
                                disabled={loading}
                                className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 px-2 py-1 hover:bg-red-50 rounded"
                              >
                                Leave Waitlist
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Complete Booking</h3>
            
            <form onSubmit={bookClub} className="space-y-3 md:space-y-4">
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Student
                </label>
                <select
                  value={bookingForm.student_school_id}
                  onChange={(e) => setBookingForm({...bookingForm, student_school_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Student</option>
                  {selectedPeriod.children.map((child) => (
                    <option key={child.school_id} value={child.school_id}>
                      {child.full_name} (Form {child.form})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact
                </label>
                <input
                  type="text"
                  value={bookingForm.emergency_contact}
                  onChange={(e) => setBookingForm({...bookingForm, emergency_contact: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Emergency contact number"
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Medical Information
                </label>
                <textarea
                  value={bookingForm.medical_info}
                  onChange={(e) => setBookingForm({...bookingForm, medical_info: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="2"
                  placeholder="Any medical conditions, allergies, or dietary requirements..."
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Special Requirements
                </label>
                <textarea
                  value={bookingForm.special_requirements}
                  onChange={(e) => setBookingForm({...bookingForm, special_requirements: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="2"
                  placeholder="Any other information or requirements..."
                />
              </div>

              <div className="flex justify-end space-x-2 md:space-x-3 pt-3 md:pt-4">
                <button
                  type="button"
                  onClick={() => setShowBookingForm(null)}
                  className="px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {loading ? 'Booking...' : 'Confirm Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ParentClubBooking;
